import { useEffect, useLayoutEffect, useReducer } from 'react'
import { checkResizeObserver } from './checkResizeObserver.js'

// check if the browser supports the ResizeObserver API
// otherwise, alert the user
checkResizeObserver()

export type ElementSize = {
  width: number
  height: number
  resizing: boolean
}

export type ElementSizeAction =
  | { type: 'resize'; width: number; height: number }
  | { type: 'startResizing' }
  | { type: 'endResizing' }

function messengerReducer(state: ElementSize, action: ElementSizeAction) {
  const { type } = action

  switch (type) {
    case 'resize': {
      const { width, height } = action
      return { ...state, height, width }
    }

    case 'startResizing':
      return { ...state, resizing: true }

    case 'endResizing':
      return { ...state, resizing: false }

    default:
      throw Error(`Unknown action: ${JSON.stringify(action)}`)
  }
}

/**
 * A hook used to monitor the size of a target `HTMLElement` using the `ResizeObserver` API.
 * The hook also take care of limiting the amount of resize event emitted throttling the events with `requestAnimationFrame`.
 *
 * @example
 * import { useCallback, useRef } from 'react'
 * import { useElementSize } from '@workwave-tidal/core/hooks/useElementSize'
 *
 * function MyComp() {
 *   const rTarget = useRef()
 *   const handleSingleClick = useCallback(() => void console.log(`I'm a single click`), [])
 *
 *   const { width, height, resizing } = useElementSize(rTarget)
 *
 *   const background = resizing ? '#666600' : '#006600'
 *
 *   return (
 *     <div
 *       style={{
 *         width: '100%',
 *         height: '100%',
 *         display: 'flex',
 *         position: 'relative',
 *         alignItems: 'center',
 *         justifyContents: 'center',
 *         background,
 *       }}
 *     >
 *       {width}x{height}
 *     </div>
 *   )
 * }
 *
 * @param element The target `HTMLElement` to monitor.
 * @param [resizingThreshold] The throttling value. Defaults to 500 milliseconds.
 * @returns An `ElementSize` object with the current size of the target element.
 */
export function useElementSize(
  element: HTMLElement | undefined | null,
  resizingThreshold = 500,
): Readonly<ElementSize> {
  const [elementSize, dispatch] = useReducer(messengerReducer, {
    height: element?.offsetHeight ?? 0,
    resizing: false,
    width: element?.offsetWidth ?? 0,
  })

  const { width, height } = elementSize

  useLayoutEffect(() => {
    if (!element?.parentElement) return

    let rafId: number | undefined

    let observer: ResizeObserver | null = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (entries.length === 0) {
        throw new Error('No UI elements registered to the useElementSize. This is a no-op.')
      }

      if (entries.length > 1) {
        throw new Error('Multiple UI elements registered to the useElementSize. This is a no-op.')
      }

      const entry = entries[0]

      // see: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
      // see: https://github.com/souporserious/react-measure/issues/104#issuecomment-418270591
      // see: https://github.com/hshoff/vx/pull/335/files
      rafId = window.requestAnimationFrame(() => {
        const {
          contentRect: { width, height },
        } = entry

        dispatch({ height, type: 'resize', width })
      })
    })

    observer.observe(element.parentElement)

    return () => {
      observer?.observe(element)
      observer?.disconnect()
      observer = null

      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [element])

  useEffect(() => {
    // NOTE: we want `width` and `height` to re-run the effect
    width
    height

    // disable resizing if the threshold is equal or lower than zero
    if (resizingThreshold <= 0) return

    dispatch({ type: 'startResizing' })

    // disable resizing after the given threshold
    const timer = setTimeout(() => {
      dispatch({ type: 'endResizing' })
    }, resizingThreshold)

    // clear the timeout when the effect is cleared
    return () => {
      clearTimeout(timer)
    }
  }, [resizingThreshold, width, height])

  return elementSize
}
